import { Controller } from "@hotwired/stimulus"
import Mailcheck from "mailcheck"

const EMAIL_REGEXP = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

export default class extends Controller {
  static get targets() {
    return [
      "emailInput",
      "validation",
      "suggestion",
      "suggestionEmail",
      "dependentCheckbox",
      "responsibleContainer",
      "responsibleSelect",
      "responsibleHiddenInput"
    ]
  }

  static get values() {
    return {
      allowDependentRegistrations: Boolean,
      isDependentRegistration: Boolean,
      email: String,
      responsibleRegistrationId: String,
      nonDependentRegistrations: Array
    }
  }

  connect() {
    this.forceValidationToShow = false
    this.hasShownValidation = false
    this.suggestionValue = null

    if (!this.hasEmailValue && this.hasEmailInputTarget) {
      this.emailValue = this.emailInputTarget.value
    }

    if (
      this.isDependentRegistrationValue &&
      !this.responsibleRegistrationIdValue &&
      this.hasNonDependentRegistrations()
    ) {
      this.responsibleRegistrationIdValue = String(this.nonDependentRegistrationsValue[0].id)
    }

    this.syncUi()
  }

  handleEmailInput(event) {
    this.emailValue = event.currentTarget.value
    this.suggestionValue = null
    this.renderSuggestion()
    this.renderValidation()
  }

  handleEmailBlur(event) {
    if (!this.isDependentRegistrationValue) {
      this.suggestionValue = this.checkEmail(event.currentTarget.value)
      this.forceValidationToShow = true
      this.hasShownValidation = true
    }

    this.renderSuggestion()
    this.renderValidation()
  }

  toggleDependentRegistration(event) {
    this.isDependentRegistrationValue = event.currentTarget.checked

    if (this.isDependentRegistrationValue) {
      this.emailValue = ""
      if (this.hasNonDependentRegistrations()) {
        this.responsibleRegistrationIdValue = String(this.nonDependentRegistrationsValue[0].id)
      }
    } else {
      this.responsibleRegistrationIdValue = ""
    }

    this.suggestionValue = null
    this.syncUi()
  }

  updateResponsibleRegistration(event) {
    this.responsibleRegistrationIdValue = event.currentTarget.value
    this.syncUi()
  }

  acceptSuggestion(event) {
    event.preventDefault()

    if (!this.suggestionValue) {
      return
    }

    this.emailValue = this.suggestionValue.full
    this.suggestionValue = null
    this.forceValidationToShow = false
    this.syncUi()
  }

  syncUi() {
    if (this.hasEmailInputTarget) {
      this.emailInputTarget.value = this.emailValue || ""
      this.emailInputTarget.readOnly = this.isDependentRegistrationValue
      this.emailInputTarget.classList.toggle("field-error", this.showEmailValidationError())
    }

    if (this.hasDependentCheckboxTarget) {
      this.dependentCheckboxTarget.checked = this.isDependentRegistrationValue
    }

    if (this.hasResponsibleContainerTarget) {
      this.responsibleContainerTarget.hidden = !(
        this.allowDependentRegistrationsValue &&
        this.isDependentRegistrationValue &&
        this.hasNonDependentRegistrations()
      )
    }

    if (this.hasResponsibleSelectTarget && this.responsibleRegistrationIdValue) {
      this.responsibleSelectTarget.value = this.responsibleRegistrationIdValue
    }

    if (this.hasResponsibleHiddenInputTarget) {
      this.responsibleHiddenInputTarget.value = this.responsibleRegistrationIdValue || ""
    }

    this.renderSuggestion()
    this.renderValidation()
  }

  renderSuggestion() {
    if (!this.hasSuggestionTarget || !this.hasSuggestionEmailTarget) {
      return
    }

    const shouldShow = !!this.suggestionValue && !this.isDependentRegistrationValue

    this.suggestionTarget.hidden = !shouldShow
    this.suggestionEmailTarget.textContent = shouldShow ? this.suggestionValue.full : ""
  }

  renderValidation() {
    if (!this.hasValidationTarget) {
      return
    }

    const shouldShow = this.showEmailValidationError()
    this.validationTarget.hidden = !shouldShow
  }

  showEmailValidationError() {
    if (this.isDependentRegistrationValue) {
      return false
    }

    const shouldValidate = (
      ((this.emailValue || "").length > 3 && this.hasShownValidation) ||
      this.forceValidationToShow
    )

    return shouldValidate && !this.isValidEmailAddress(this.emailValue)
  }

  isValidEmailAddress(email) {
    return email && EMAIL_REGEXP.test(email)
  }

  hasNonDependentRegistrations() {
    return Array.isArray(this.nonDependentRegistrationsValue) && this.nonDependentRegistrationsValue.length > 0
  }

  checkEmail(email) {
    let suggestion = null

    Mailcheck.run({
      email,
      suggested(result) {
        suggestion = result
      },
      empty() {}
    })

    return suggestion
  }
}
